import React, { useEffect, useMemo, useState } from "react";
import { Form, InputNumber } from "antd";
import { useTranslation } from "react-i18next";
import CustomModal from "../../../components/Common/CustomModal";
import CommonSelectLite from "components/Common/CommonSelectLite";
import LocalizedDatePicker from "components/Common/ui/LocalizedDatePicker";
import { CashBox, CashBoxCreatePayload } from "../../../types/cashBoxTypes";
import { parseSQLDate } from "utils/dateUtils";

interface AddEditCashBoxModalProps {
  isOpen: boolean;
  onClose: () => void;
  cashBoxData?: CashBox | null;
  onSave: (payload: CashBoxCreatePayload) => Promise<Boolean>;
  users: any[];
}

const AddEditCashBoxModal: React.FC<AddEditCashBoxModalProps> = ({
  isOpen,
  onClose,
  cashBoxData,
  onSave,
  users,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const payload: CashBoxCreatePayload = {
        userId: values.userId,
        day: values.day.format("YYYY-MM-DD"),
        initialBalance: values.initialBalance,
      };

      const res = await onSave(payload);
      if (res) {
        form.resetFields();
        onClose();
      }

    } catch (err) {
      console.error("Failed to save cashbox:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cashBoxData) {
      form.setFieldsValue({
        userId: cashBoxData.userId,
        day: cashBoxData.day ? parseSQLDate(cashBoxData.day) : null,
        initialBalance: cashBoxData.initialBalance,
      });
    } else {
      form.resetFields();
    }
  }, [cashBoxData]);

  const userOptions = useMemo(
    () => (users || []).map((lt: any) => ({ value: lt.id, label: lt.firstName + "" + lt.lastName })),
    [users]
  );
  return (
    <CustomModal
      title={cashBoxData ? t("cashbox.edit_title") : t("cashbox.add_title")}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      saveText={cashBoxData ? t("save") : t("add")}
    >
      <Form layout="vertical" form={form}>
        <Form.Item
          label={t("cashbox.fields.user")}
          name="userId"
          rules={[
            { required: true, message: t("cashbox.validation.user_required") },
          ]}
        >
          <CommonSelectLite
            placeholder={t("cashbox.fields.select_user")}
            options={userOptions}
            optionValueKey="value"
            optionLabelKey="label"
          />
        </Form.Item>

        <Form.Item
          label={t("cashbox.fields.day")}
          name="day"
          rules={[
            { required: true, message: t("cashbox.validation.day_required") },
          ]}
        >
          <LocalizedDatePicker className="w-100"
            placeholder={t("cashbox.fields.select_day")} />
        </Form.Item>

        <Form.Item
          label={t("cashbox.fields.initial_balance")}
          name="initialBalance"
          rules={[
            {
              required: true,
              message: t("cashbox.validation.initial_balance_required"),
            },
          ]}
        >
          <InputNumber
            className="w-100"
            min={0}
            precision={2}
            addonAfter="MAD"
            placeholder={t("cashbox.fields.initial_balance")}
          />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default AddEditCashBoxModal;
